import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import bp from 'styled-components-breakpoint';
import {
  Facebook,
  FacebookFilled,
  Instagram,
  InstagramFilled,
  Mail,
  Twitter,
  TwitterFilled,
} from '../Icons';

/**
 * Map type to social icon
 */
const socialMap = {
  email: {
    Icon: Mail,
    defaultUrl: 'mailto:yoursoulmatters@soul-cycle.com',
  },
  facebook: {
    Icon: Facebook,
    AltIcon: FacebookFilled,
    defaultUrl: 'https://www.facebook.com/pages/SoulCycle/64759509591',
  },
  instagram: {
    Icon: Instagram,
    AltIcon: InstagramFilled,
    defaultUrl: 'https://instagram.com/soulcycle',
  },
  twitter: {
    Icon: Twitter,
    AltIcon: TwitterFilled,
    defaultUrl: 'https://twitter.com/soulcycle',
  },
};

const StyledSocialLink = styled.a`
  color: ${(props) => props.theme.colors.grayscale.black};
  display: inline-block;
  text-decoration: none;

  svg {
    display: inline-block;
    vertical-align: middle;
  }
`;

const StyledLabel = styled.span`
  ${(props) => props.theme.typography.buttonSocial};
  display: none;
  ${bp('tablet')`
    display: inline-block;
    padding: 0 0 0 10px;
    vertical-align: middle;
  `}
`;

/**
 * Gets the SVG corresponding to the type
 *
 * @param {string} type social button to use
 *
 * @returns {Object} object representing the tag and properties to use
 */
const getSocial = (type) => {
  const element = socialMap[type];
  if (typeof element === 'undefined') {
    // TODO: PE-8208 - Replace console.error with a logger service call
    console.error('Invalid type data in SocialButton component:', type);
    return {};
  }

  return element;
};

/**
 * Renders a social button component for one of the supported social methods.
 */
function SocialButton(props) {
  const { label, type, href, onClick, filled, ...rest } = props;
  const { AltIcon = null, Icon = null, defaultUrl = null } = getSocial(type);
  const finalHref = href || defaultUrl;

  return (
    <StyledSocialLink
      href={finalHref}
      target="_blank"
      onClick={onClick}
      aria-label={label}
      rel="noopener nofollow"
      {...rest}
    >
      {filled ? AltIcon && <AltIcon /> : Icon && <Icon />}
      {label && <StyledLabel>{label}</StyledLabel>}
    </StyledSocialLink>
  );
}

/**
 * propTypes for this component
 */
SocialButton.propTypes = {
  /**
   *  type of social method
   */
  type: PropTypes.oneOf(['facebook', 'instagram', 'email', 'twitter'])
    .isRequired,
  /**
   *  URL to link to if not the default
   */
  href: PropTypes.string,
  /**
   *  label to display
   */
  label: PropTypes.string,
  /**
   * Click handler
   */
  onClick: PropTypes.func,
  /**
   * Filled or hollow version
   */
  filled: PropTypes.bool,
};

/**
 * Default prop types
 */
SocialButton.defaultProps = {
  href: '',
  label: '',
  onClick: undefined,
  filled: false,
};

/* @component */
export default SocialButton;
